import { useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/components/ui/Toast';
import { useBranding } from '@/lib/branding';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Returns a function to launch Razorpay checkout for a booking or membership.
 * Handles both live (Razorpay test/live keys) and mock (no keys) flows.
 *
 * Sub-merchant model: when the backend returns an account_id, the order lives
 * on the venue's own Razorpay account (created with the platform's partner
 * key) and Checkout must be opened with that account_id — the customer pays
 * the venue directly.
 *
 * usage: const pay = usePayment(); pay({ purpose: 'booking', reference_id, onSuccess })
 */
export function usePayment() {
  const { user } = useAuth();
  const { toast } = useToast();
  // Fix (branding audit): the Razorpay checkout name/theme were hardcoded
  // "KBA"/"#CCFF00" rather than reading the configured app name/accent
  // color — the one other spot besides the CSS accent color that "KBA" was
  // still baked in outside of lib/branding.jsx's reach.
  const { app_name, primary_color } = useBranding();

  return useCallback(async ({ purpose, reference_id, onSuccess }) => {
    // If /verify fails after the customer has actually paid (network drop,
    // timeout), the payment webhook usually confirms it within seconds —
    // poll before telling them anything went wrong, so they don't pay twice.
    const waitForConfirmation = async () => {
      for (let i = 0; i < 6; i += 1) {
        await sleep(2000);
        try {
          const { data } = await api.get('/payments/status', { params: { purpose, reference_id } });
          if (data.paid) return true;
        } catch { /* keep polling */ }
      }
      return false;
    };

    try {
      const { data: order } = await api.post('/payments/create-order', { purpose, reference_id });

      const verify = async (paymentResp) => {
        try {
          await api.post('/payments/verify', {
            razorpay_order_id: paymentResp.razorpay_order_id || order.order_id,
            razorpay_payment_id: paymentResp.razorpay_payment_id,
            razorpay_signature: paymentResp.razorpay_signature || 'mock_signature',
            purpose,
            reference_id,
          });
          toast({ title: 'Payment confirmed', description: 'Confirmation sent to your email', variant: 'success' });
          onSuccess && onSuccess();
        } catch (e) {
          if (e.response?.status === 409) {
            // e.g. paid after the hold expired and the slot was taken — the
            // backend has already refunded; show its explanation as-is.
            toast({ title: 'Payment not applied', description: e.response?.data?.detail || 'Please contact support', variant: 'error' });
            return;
          }
          toast({ title: 'Payment received — confirming…', description: 'This can take a few seconds. Please don’t pay again.', variant: 'default' });
          if (await waitForConfirmation()) {
            toast({ title: 'Payment confirmed', description: 'Confirmation sent to your email', variant: 'success' });
            onSuccess && onSuccess();
          } else {
            toast({
              title: 'Still confirming your payment',
              description: 'If money was deducted, your booking will be confirmed automatically shortly. Check My Bookings before paying again.',
              variant: 'error',
            });
          }
        }
      };

      if (order.mode === 'mock') {
        // demo path: confirm immediately
        toast({ title: 'Demo payment', description: 'No Razorpay keys configured — completing as test.', variant: 'default' });
        await verify({
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
        });
        return;
      }

      if (!window.Razorpay) {
        toast({ title: 'Checkout unavailable', description: 'Razorpay script failed to load', variant: 'error' });
        return;
      }
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: order.venue_name || app_name,
        description: purpose === 'booking' ? 'Court Booking' : 'Membership Purchase',
        order_id: order.order_id,
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
        // Explicitly enable all payment methods so UPI / Cards / Netbanking / Wallets all show
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
          emi: true,
          paylater: true,
        },
        // Surface UPI and Cards as the most prominent blocks in checkout
        config: {
          display: {
            blocks: {
              banks: {
                name: 'Pay using UPI / Cards / Netbanking',
                instruments: [
                  { method: 'upi' },
                  { method: 'card' },
                  { method: 'netbanking' },
                  { method: 'wallet' },
                ],
              },
            },
            sequence: ['block.banks'],
            preferences: { show_default_blocks: true },
          },
        },
        theme: { color: primary_color },
        handler: verify,
        modal: { ondismiss: () => toast({ title: 'Payment cancelled', variant: 'default' }) },
      };
      if (order.account_id) options.account_id = order.account_id;
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast({ title: 'Payment failed', description: resp?.error?.description || 'Please try again', variant: 'error' });
      });
      rzp.open();
    } catch (e) {
      toast({ title: 'Order failed', description: e.response?.data?.detail || 'Try again', variant: 'error' });
    }
  }, [user, toast, app_name, primary_color]);
}
